import Links from '@/shared/constants/navegacao';
import { Link } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { LotacaoService } from '@/modules/business/lotacoes/lotacao.service';
import { ReadLotacaoDto } from '@/api.types';
import { SearchDtoLotacoes } from '@/shared/types/search.dto';
import { DataAmigavel } from '@/shared/utils/datas';
import { TipoLotacaoEstagioList, TipoLotacaoList } from '@/shared/constants/enumlist';

const TIPO_LOTACAO_LIST = TipoLotacaoList.concat(TipoLotacaoEstagioList);

type EstagiariosResponsavelListaProps = {
  pessoaId?: string | null;
  /** Texto quando não há estagiários vigentes sob responsabilidade. */
  emptyMessage?: string;
};

/** Lista somente leitura: estagiários cuja lotação aponta esta pessoa como responsável. */
export function EstagiariosResponsavelLista({
  pessoaId,
  emptyMessage = 'Nenhum estagiário com lotação vigente sob responsabilidade desta pessoa.',
}: EstagiariosResponsavelListaProps) {
  const [lotacoes, setLotacoes] = useState<ReadLotacaoDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    if (!pessoaId) {
      setLotacoes([]);
      setErro(null);
      return;
    }
    setLoading(true);
    setErro(null);
    try {
      const lotacaoService = new LotacaoService();
      const searchParams: SearchDtoLotacoes = {
        skip: 0,
        take: 9000,
        id_responsavel: pessoaId,
        tipo_pessoa: 'estagiario',
        somente_ativas: true,
        vigente: true,
      } as SearchDtoLotacoes;
      const [lista] = await lotacaoService.Enumerate(searchParams);
      setLotacoes(lista ?? []);
    } catch (error) {
      setLotacoes([]);
      setErro(error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
    }
  }, [pessoaId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (!pessoaId) {
    return (
      <p className="text-muted small mb-0 py-1">
        Salve o cadastro da pessoa para listar os estagiários sob responsabilidade.
      </p>
    );
  }

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 py-2 text-muted small">
        <Spinner animation="border" size="sm" />
        Carregando estagiários…
      </div>
    );
  }

  if (erro) {
    return <p className="text-danger small mb-0 py-1">{erro}</p>;
  }

  if (lotacoes.length === 0) {
    return <p className="text-muted small mb-0 py-1">{emptyMessage}</p>;
  }

  return (
    <table className="table table-sm table-bordered table-hover table-striped mb-0">
      <thead>
        <tr>
          <th>Estagiário</th>
          <th>Tipo</th>
          <th className="text-center">Início</th>
          <th className="text-center">Término</th>
          <th>Local</th>
        </tr>
      </thead>
      <tbody>
        {lotacoes.map((lotacao, index) => {
          const estagiario = lotacao.estagio?.estagiario;
          const nome =
            (estagiario as { nome?: string } | undefined)?.nome ||
            estagiario?.pessoa?.nome ||
            '-';
          const pessoaEstagiarioId = estagiario?.pessoa?.id;
          const local =
            lotacao.defensoria?.nome || lotacao.nucleo?.nome || lotacao.setor?.nome || '-';

          return (
            <tr key={lotacao.id ?? index}>
              <td className="text-break">
                {pessoaEstagiarioId ? (
                  <Link
                    to={`${Links.Pessoa_Editar}/${pessoaEstagiarioId}`}
                    title="Abrir cadastro do estagiário"
                  >
                    {nome}
                  </Link>
                ) : (
                  nome
                )}
              </td>
              <td>{TIPO_LOTACAO_LIST.find((a) => a.value === lotacao.tipo)?.name ?? '-'}</td>
              <td className="text-center">
                {lotacao.data_inicial ? DataAmigavel(lotacao.data_inicial) : '-'}
              </td>
              <td className="text-center">
                {lotacao.data_final ? DataAmigavel(lotacao.data_final) : 'Em aberto'}
              </td>
              <td>
                {lotacao.id ? (
                  <Link to={`${Links.Vinculos_Editar}/${lotacao.id}`} title="Ver lotação">
                    {local}
                  </Link>
                ) : (
                  local
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
